import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";

import { ErrorIcon } from "../../../assets/icons/errorIcon";
import { useNearViewport } from "../../../hooks/useNearViewport";
import { LoaderWithText } from "../../../shared/Loader";

type TCSVRendererProps = {
	blobLink: string;
	description?: string;
};

const CSVRenderer: React.FC<TCSVRendererProps> = ({
	blobLink,
	description,
}) => {
	const [data, setData] = useState<Array<Array<string>>>(
		[]
	);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const { containerRef, isNearViewport } =
		useNearViewport();

	useEffect(() => {
		if (!isNearViewport) {
			return;
		}

		let isCancelled = false;
		const controller = new AbortController();
		const timeoutId = setTimeout(
			() => {
				controller.abort();
			},
			30000
		);

		const fetchData = async () => {
			try {
				if (!isCancelled) {
					setError(null);
					setLoading(true);
				}

				const response = await fetch(blobLink, {
					signal: controller.signal,
				});

				clearTimeout(timeoutId);

				if (!response.ok) {
					throw new Error(
						`Failed to fetch data: ${response.status} ${response.statusText}`
					);
				}
				const text = await response.text();

				// Split by newline
				const lines = text.split(/\r?\n/);

				// Regex to split by comma ONLY if not inside quotes
				// This handles "5,30,298" correctly as a single value
				const parseLine = (line: string) => {
					const regex =
						/,(?=(?:(?:[^"]*"){2})*[^"]*$)/;
					return line.split(regex).map((val) => {
						// Remove surrounding quotes if present
						val = val.trim();
						if (val.startsWith('"') && val.endsWith('"')) {
							return val.slice(1, -1).replace(/""/g, '"');
						}
						return val;
					});
				};

				const rows = lines.map(parseLine);
				if (!isCancelled) {
					setData(rows);
					setLoading(false);
				}
			} catch (err: any) {
				if (isCancelled) {
					return;
				}
				console.error("Error fetching blob data:", err);
				if (err.name === "AbortError") {
					setError(
						"Request timed out. The data might be too large or the link expired."
					);
				} else {
					setError(
						`Failed to load data: ${err.message || "Unknown error"}`
					);
				}
				setLoading(false);
			} finally {
				clearTimeout(timeoutId);
			}
		};

		if (blobLink) {
			// Validate blobLink format
			try {
				// eslint-disable-next-line no-new
				new URL(blobLink);
				void fetchData();
			} catch (urlErr: unknown) {
				console.error("Invalid blob link:", blobLink, urlErr);
				setError("Invalid data link provided");
				setLoading(false);
			}
		}

		return () => {
			isCancelled = true;
			controller.abort();
			clearTimeout(timeoutId);
		};
	}, [blobLink, isNearViewport]);

	const headers = data[0] ?? [];
	const rows: Array<Record<string, string | number>> = data
		.slice(1)
		.filter((row) =>
			row.some((cell) => cell.trim().length > 0)
		)
		.map((row, rowIndex) => {
			const rowObj: Record<string, string | number> = {
				id: rowIndex,
			};
			headers.forEach((_, cellIndex) => {
				rowObj[`col${cellIndex}`] = row[cellIndex] ?? "";
			});
			return rowObj;
		});
	const columns: Array<GridColDef> = headers.map(
		(header, index) => ({
			field: `col${index}`,
			headerName: header,
			flex: 1,
			minWidth: 160,
		})
	);
	const defaultPageSize =
		rows.length > 0 ? Math.min(rows.length, 10) : 10;

	if (!isNearViewport) {
		return (
			<div
				ref={containerRef}
				className="artifact-deferred-placeholder"
			>
				<span className="artifact-deferred-placeholder__eyebrow">
					Table artifact
				</span>
				<p className="artifact-deferred-placeholder__title">
					Table preview will load when you scroll here.
				</p>
				{description && (
					<p className="artifact-deferred-placeholder__body">
						{description}
					</p>
				)}
			</div>
		);
	}

	if (loading) {
		return (
			<div className="blob-data-loader">
				<LoaderWithText text="Loading data..." />
			</div>
		);
	}

	if (error) {
		return (
			<div className="blob-data-error">
				<ErrorIcon />
				<span>{error}</span>
			</div>
		);
	}

	if (data.length === 0) {
		return (
			<div className="blob-data-empty">
				No data available
			</div>
		);
	}

	return (
		<div className="blob-data-container">
			<div className="blob-data-grid">
				<DataGrid
					rows={rows}
					columns={columns}
					autoHeight
					disableRowSelectionOnClick
					disableColumnMenu
					density="compact"
					initialState={{
						pagination: {
							paginationModel: {
								page: 0,
								pageSize: defaultPageSize,
							},
						},
					}}
					pageSizeOptions={[10, 25, 50, 100]}
				/>
			</div>
			{description && (
				<p className="blob-data-caption">
					{description}
				</p>
			)}
		</div>
	);
};

export default CSVRenderer;
